/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Heading, HStack, VStack } from '@chakra-ui/react';
import {
  useBookCategories,
  useGetFavoritedBooksMutation,
  useMothlyBestSellers,
  useMyReadingBooks,
} from '@partiu/api/bookstore/hooks';
import { useEffect, useMemo, useState } from 'react';
import { AiFillAppstore, AiOutlineRead, AiOutlineSearch } from 'react-icons/ai';
import { SlEarphones, SlHeart } from 'react-icons/sl';
import { useSearchParams } from 'react-router-dom';
import { CategoryTag } from '../../../../packages/ui/components/atoms/CategoryTag';
import { Input } from '../../../../packages/ui/components/atoms/Input';
import { useTheme } from '../../../../packages/ui/contexts/ThemeProvider';
import { BookGrid } from '../components/organisms/BookGrid';
import { TopBookstoreMenu } from '../components/organisms/TopBookstoreMenu';
import { MainTemplate } from '../templates/MainTemplate';
import { BooksOfTheMonthSection } from './Home';
import { GoStar } from 'react-icons/go';
import { ReceivedBooksTemplate } from './ReceivedBooks/components/ReceivedBookTemplate';
import { GiStarsStack } from 'react-icons/gi';
import { HorizontalBooks } from '../components/molecules/HorizontalBooks';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';

interface Category {
  id: number;
  name: string;
}

function useCategoryTags(
  categories: { results: Category[] } | undefined,
  tagSet: Set<number>,
  setTagSet: React.Dispatch<React.SetStateAction<Set<number>>>
) {
  return useMemo(
    () =>
      categories?.results
        .map((c) => ({ ...c, active: tagSet.has(c.id) ? 1 : 0 }))
        .sort((a, b) => b.active - a.active)
        .map((c) => (
          <CategoryTag
            key={c.id}
            active={tagSet.has(c.id)}
            onClick={() => {
              setTagSet((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(c.id)) {
                  newSet.delete(c.id);
                } else {
                  newSet.add(c.id);
                }
                return newSet;
              });
            }}
          >
            {c.name.toLocaleLowerCase()}
          </CategoryTag>
        )),
    [tagSet, categories?.results, setTagSet]
  );
}

interface TabContentProps {
  children: React.ReactNode;
}

function TabContent({ children }: TabContentProps) {
  return (
    <TabPanel padding="0px">
      <VStack mt="20px" width="100%" spacing={0}>
        {children}
      </VStack>
    </TabPanel>
  );
}

interface BookstoreTabProps {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  tags: React.ReactNode;
  description?: string;
  recurringBook: boolean;
  filters?: Record<string, string>;
}

function BookstoreTab({ search, description, setSearch, tags, recurringBook, filters }: BookstoreTabProps) {
  return (
    <TabContent>
      <VStack spacing={5} width="100%" align="flex-start">
        {filters?.type === 'book' && (
          <HorizontalBooks
            hookApi={useMothlyBestSellers}
            name="Destaques do mês"
            description="Confira os livros que estão em alta e conquistando leitores."
            params={{}}
          />
        )}
        <BooksOfTheMonthSection audiobook={filters?.type === 'audiobook'} recurringBook={recurringBook} />

        <Heading paddingLeft={'5%'} as="h3" size="md">
          {description}
        </Heading>
        <HStack
          paddingX="5%"
          w="100%"
          overflowX="auto"
          css={{
            '&::-webkit-scrollbar': { display: 'none' },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
            '@media (min-width: 768px)': {
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              overflowX: 'unset',
            },
          }}
        >
          {tags}
        </HStack>
        <VStack paddingX="5%" spacing={2} paddingBottom="80px" width="100%" margin="auto">
          <Input value={search} onValueChange={setSearch} label="Busca" rightElement={<AiOutlineSearch />} />
          <BookGrid filters={{ ...filters }} />
        </VStack>
      </VStack>
    </TabContent>
  );
}

function RecebidosTab() {
  const { colors, isNative, bookstoreTypes, slug } = useTheme();

  return (
    <TabContent>
      <Tabs variant="soft-rounded" colorScheme="teal" width="100%">
        <TabList>
          <Box
            alignItems={'center'}
            justifyContent={'center'}
            display={'flex'}
            flexDir={'row'}
            width={'100%'}
            paddingBottom="10px"
          >
            <Tab
              _selected={{
                color: 'white',
                bg: colors.primaryColor,
                borderColor: colors.primaryColor,
              }}
              _focus={{
                boxShadow: '0 0 0 3px var(--chakra-colors-primary-300)',
              }}
            >
              {bookstoreTypes.includes('magazine') ? 'Revistas' : 'Livros'}
            </Tab>
            {isNative && bookstoreTypes.includes('audiobook') && (
              <Tab
                _selected={{
                  color: 'white',
                  bg: colors.primaryColor,
                  borderColor: colors.primaryColor,
                }}
                _focus={{
                  boxShadow: '0 0 0 3px var(--chakra-colors-primary-300)',
                }}
              >
                Áudio Books
              </Tab>
            )}
          </Box>
        </TabList>
        <TabPanels>
          <TabContent>
            <ReceivedBooksTemplate
              description={
                ['abrintbiblioteca', 'abrintrevistas'].includes(slug)
                  ? `Confira ${
                      bookstoreTypes.includes('magazine') ? 'nossas revistas' : 'nossos livros'
                    } dos mêses e aproveite!`
                  : 'Abaixo estão os livros do mês, adquiridos como parte do seu combo e são seus para sempre!'
              }
              filter={{
                book__type: bookType[bookstoreTypes[0]],
              }}
            />
          </TabContent>
          {isNative && bookstoreTypes.includes('audiobook') && (
            <TabContent>
              <ReceivedBooksTemplate
                description={
                  ['abrintbiblioteca', 'abrintrevistas'].includes(slug)
                    ? 'Confira nossos audiolivros dos mêses e aproveite!'
                    : 'Abaixo estão os audiolivros do mês, adquiridos como parte do seu combo e são seus para sempre!'
                }
                filter={{
                  book__type: 'audiobook',
                }}
              />
            </TabContent>
          )}
        </TabPanels>
      </Tabs>
    </TabContent>
  );
}

const bookType: any = {
  book: 'book',
  audiobook: 'audiobook',
  magazine: 'magazine',
};

export function BookstorePage() {
  const { bookstoreTypes, recurringBook, isNative, showBestSellers } = useTheme();
  const { data: categories } = useBookCategories();
  const [tagSet, setTagSet] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState<string>('');
  const [params] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState(
    params.get('bestsellers') === 'true' ? (bookstoreTypes.includes('audiobook') ? 2 : 1) : 0
  );

  useEffect(() => {
    const categoryParam = params.get('categorias');
    if (!categoryParam) return;

    setTimeout(() => {
      setTagSet(new Set(categoryParam.split(',').map((cid) => parseInt(cid))));
    }, 800);
  }, [params]);

  const tags = useCategoryTags(categories, tagSet, setTagSet);

  return (
    <MainTemplate noShadow hideTopMenu>
      <TopBookstoreMenu
        selectedTab={selectedTab}
        tabs={[
          ...(bookstoreTypes.includes('book') ? [{ name: 'Home', icon: <AiFillAppstore /> }] : []),
          ...(bookstoreTypes.includes('book') && showBestSellers
            ? [{ name: 'Best Sellers', icon: <GiStarsStack /> }]
            : []),
          ...(bookstoreTypes.includes('audiobook') ? [{ name: 'Audiolivros', icon: <SlEarphones /> }] : []),
          ...(bookstoreTypes.includes('magazine') ? [{ name: 'Revistas', icon: <AiFillAppstore /> }] : []),
          ...(isNative
            ? [
                { name: 'Recebidos', icon: <GoStar /> },
                { name: 'Lendo', icon: <AiOutlineRead /> },
                { name: 'Favoritos', icon: <SlHeart /> },
              ]
            : []),
        ]}
      >
        {bookstoreTypes.includes('book') && (
          <BookstoreTab
            search={search}
            description="Explore nosso Acervo"
            setSearch={setSearch}
            filters={{
              search,
              categories: Array.from(tagSet).join(','),
              type: 'book',
              random: 'true',
              limit: '100',
            }}
            tags={tags}
            recurringBook={recurringBook}
          />
        )}
        {bookstoreTypes.includes('book') && showBestSellers && (
          <TabContent>
            <VStack spacing={5} width="100%" align="flex-start">
              <HorizontalBooks
                hookApi={useMothlyBestSellers}
                name="Destaques do mês"
                description="Confira os livros que estão em alta e conquistando leitores."
                params={{}}
              />
              <Heading paddingLeft={'5%'} as="h3" size="md">
                Explore nossos Best Sellers
              </Heading>
              <HStack
                paddingX="5%"
                w="100%"
                overflowX="auto"
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                  '@media (min-width: 768px)': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    overflowX: 'unset',
                  },
                }}
              >
                {tags}
              </HStack>

              <VStack paddingX="5%" spacing={2} paddingBottom="80px" width="100%" margin="auto">
                <Input value={search} onValueChange={setSearch} label="Busca" rightElement={<AiOutlineSearch />} />
                <BookGrid
                  filters={{
                    search,
                    categories: Array.from(tagSet).join(','),
                    type: 'book',
                    is_best_seller: true,
                  }}
                />
              </VStack>
            </VStack>
          </TabContent>
        )}
        {bookstoreTypes.includes('audiobook') && (
          <BookstoreTab
            search={search}
            description="Explore nossos audiolivros"
            tags={tags}
            setSearch={setSearch}
            filters={{
              search,
              categories: Array.from(tagSet).join(','),
              type: 'audiobook',
            }}
            recurringBook={recurringBook}
          />
        )}

        {bookstoreTypes.includes('magazine') && (
          <BookstoreTab
            search={search}
            setSearch={setSearch}
            description="Explore nossa banca"
            filters={{
              search,
              categories: Array.from(tagSet).join(','),
              type: 'magazine',
              limit: '100',
              random: 'true',
            }}
            tags={tags}
            recurringBook={recurringBook}
          />
        )}

        {isNative && <RecebidosTab />}
        {isNative && (
          <TabContent>
            <HStack paddingX="5%" w="100%" overflowX="auto">
              <BookGrid mutationHook={useMyReadingBooks} />
            </HStack>
          </TabContent>
        )}
        {isNative && (
          <TabContent>
            <HStack paddingX="5%" w="100%" overflowX="auto">
              <BookGrid mutationHook={useGetFavoritedBooksMutation} />
            </HStack>
          </TabContent>
        )}
      </TopBookstoreMenu>
    </MainTemplate>
  );
}
